import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ActionableNotification, Button, InlineNotification, Modal, Search, Select, SelectItem, Toggle } from '@carbon/react';
import { formatVnd, GAME_GROUP_LABEL, type GameGroup } from '@ruby-cms/shared';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { BlockSkeleton, EmptyState, ErrorState, InlineError, PageHeader } from '../components/states';
import { When } from '../components/format';
import { HE_A_ERRORS } from '../components/labels';
import type { Command } from './Commands';
import { MoneyInput } from '../components/MoneyInput';

type Limit = { bat?: boolean; cuocMin?: number; cuocMax?: number };
type Row = { id: string; name: string; group: GameGroup; round?: boolean; note?: string; stopOnly?: boolean; limit: Limit | null };
type Data = { games: Row[]; meta: { version: number; source: string; at: string | null }; pending: { id: string }[] };
type Draft = { off: boolean; min: string; max: string };

const toDraft = (l: Limit | null): Draft => ({ off: l?.bat === false, min: l?.cuocMin != null ? String(l.cuocMin) : '', max: l?.cuocMax ? String(l.cuocMax) : '' });
// bat is always explicit: a save never drops a stop that is on the page
const toLimit = (d: Draft): Limit => {
  const l: Limit = { bat: !d.off };
  if (d.min) l.cuocMin = Number(d.min);
  if (d.max) l.cuocMax = Number(d.max);
  return l;
};
const same = (a: Draft, b: Draft) => a.off === b.off && a.min === b.min && a.max === b.max;

function describe(d: Draft) {
  if (d.off) return 'Tạm dừng nhận cược';
  const parts = [d.min ? 'tối thiểu ' + formatVnd(Number(d.min)) : null, d.max ? 'tối đa ' + formatVnd(Number(d.max)) : null].filter(Boolean);
  return parts.length ? parts.join(', ') : 'Theo luật game';
}

const isStale = (e: unknown) => e instanceof ApiError && e.status === 409 && e.code === 'STALE';

export function GameLimitsPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['game-limits'], queryFn: () => api.get<Data>('/api/admin/games/limits') });
  const [draft, setDraft] = useState<Map<string, Draft>>(new Map());
  // version the draft was built on, taken at the first edit; a refetch must not move it, or the
  // server's version check would pass and the save would overwrite what another operator saved
  const [base, setBase] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<Command | null>(null);
  const [text, setText] = useState('');
  const [group, setGroup] = useState('');
  const m = useMutation({
    mutationFn: () => api.put<{ command: Command }>('/api/admin/games/limits', {
      changes: Object.fromEntries([...draft].map(([id, d]) => [id, toLimit(d)])),
      expect: base ?? q.data!.meta.version,
    }),
    onSuccess: (r) => { setSaving(false); setDraft(new Map()); setBase(null); setResult(r.command); qc.invalidateQueries({ queryKey: ['game-limits'] }); },
    onError: (e) => { if (isStale(e)) setSaving(false); },
  });
  const stale = isStale(m.error);
  const discard = () => { setDraft(new Map()); setBase(null); m.reset(); qc.invalidateQueries({ queryKey: ['game-limits'] }); };

  if (q.isLoading) return <div className="page"><BlockSkeleton lines={10} /></div>;
  if (q.error || !q.data) return <div className="page"><ErrorState error={q.error} onRetry={() => q.refetch()} /></div>;
  const { games, meta, pending } = q.data;
  const byId = new Map(games.map((g) => [g.id, g]));
  const eff = (g: Row) => draft.get(g.id) ?? toDraft(g.limit);
  const editable = can('games.config');
  const edit = (g: Row, d: Draft) => {
    const n = new Map(draft);
    if (same(d, toDraft(g.limit))) n.delete(g.id); else n.set(g.id, d);
    setDraft(n);
    setBase(n.size ? (base ?? meta.version) : null);
  };
  const invalid = [...draft].filter(([, d]) => d.min && d.max && Number(d.min) > Number(d.max));
  const needle = text.trim().toLowerCase();
  const groups = [...new Set(games.map((g) => g.group))];
  const rows = games.filter((g) => (!group || g.group === group) && (!needle || g.name.toLowerCase().includes(needle) || g.id.toLowerCase().includes(needle)));

  return (
    <div className="page page--with-bar">
      <PageHeader title="Hạn mức cược"
        lead="Chặn thêm trên luật sẵn có của từng game, chỉ với tiền KRW (không áp dụng cho xu). Để trống là theo luật game. Máy chủ áp dụng ngay từ lượt cược tiếp theo, không cần khởi động lại." />
      <div className="row health">
        <span>Hạn mức: {meta.source === 'default' ? <span className="muted">chưa đặt từ CMS</span> : <>phiên bản <span className="num">{meta.version}</span>{meta.at ? <span className="muted">, <When v={meta.at} /></span> : null}</>}</span>
        {pending.length ? <span className="muted">{pending.length} lệnh đang gửi</span> : null}
      </div>
      {result ? (
        <InlineNotification kind={result.status === 'SUCCEEDED' ? 'success' : 'warning'} lowContrast onClose={() => setResult(null)}
          title={result.status === 'SUCCEEDED' ? 'Hệ A đã nhận hạn mức mới' : 'Lệnh chưa xong'}
          subtitle={result.status === 'SUCCEEDED' ? '' : (result.lastError && HE_A_ERRORS[result.lastError]) || result.lastError || result.status} />
      ) : null}
      {stale ? (
        <ActionableNotification kind="warning" inline lowContrast hideCloseButton
          title="Hạn mức vừa được người khác đổi"
          subtitle={`Bản bạn đang sửa dựa trên phiên bản ${base ?? '?'}, trên máy chủ đã là phiên bản ${meta.version}. Chưa lưu gì. Bỏ bản nháp và tải lại để xem hạn mức mới (có thể có lệnh tạm dừng), rồi sửa lại.`}
          actionButtonLabel="Bỏ bản nháp và tải lại" onActionButtonClick={discard} />
      ) : null}
      <div className="toolbar-filters">
        <Search size="md" labelText="Tìm game" placeholder="Tên hoặc mã game" value={text} onChange={(e) => setText(e.target.value)} className="toolbar-filters__grow" />
        <Select id="gl-group" labelText="Nhóm" value={group} onChange={(e) => setGroup(e.target.value)}>
          <SelectItem value="" text={`Tất cả nhóm (${games.length})`} />
          {groups.map((k) => <SelectItem key={k} value={k} text={`${GAME_GROUP_LABEL[k]} (${games.filter((g) => g.group === k).length})`} />)}
        </Select>
      </div>
      {rows.length ? <table className="plain vis">
        <thead><tr><th>Game</th><th>Nhóm</th><th>Nhận cược</th><th className="r">Cược tối thiểu</th><th className="r">Cược tối đa</th><th>Tính trên</th></tr></thead>
        <tbody>
          {rows.map((g) => {
            const d = eff(g);
            return (
              <tr key={g.id} className={draft.has(g.id) ? 'is-changed' : undefined}>
                <td className="is-title"><div className="vis__name">{g.name}</div><div className="muted num">{g.id}</div>
                  {stale && draft.has(g.id) ? <div className="muted">Trên máy chủ: {describe(toDraft(g.limit))}</div> : null}</td>
                <td data-label="Nhóm">{GAME_GROUP_LABEL[g.group]}</td>
                <td data-label="Nhận cược">
                  <Toggle id={'on-' + g.id} size="sm" labelText="" hideLabel aria-label={'Nhận cược ' + g.name} labelA="Tạm dừng" labelB="Nhận"
                    toggled={!d.off} disabled={!editable} onToggle={(on) => edit(g, { ...d, off: !on })} />
                </td>
                <td className="r" data-label="Cược tối thiểu">
                  <MoneyInput id={'min-' + g.id} size="sm" hideLabel labelText={'Cược tối thiểu ' + g.name} className="money-input" maxDigits={13}
                    placeholder="theo game" disabled={!editable || d.off || g.stopOnly} value={d.min || ''}
                    onChange={(v) => edit(g, { ...d, min: v })} />
                </td>
                <td className="r" data-label="Cược tối đa">
                  <MoneyInput id={'max-' + g.id} size="sm" hideLabel labelText={'Cược tối đa ' + g.name} className="money-input" maxDigits={13}
                    placeholder="theo game" disabled={!editable || d.off || g.stopOnly} value={d.max || ''}
                    invalid={!!(d.min && d.max && Number(d.min) > Number(d.max))} invalidText="Nhỏ hơn tối thiểu"
                    onChange={(v) => edit(g, { ...d, max: v })} />
                </td>
                <td className="muted" data-label="Tính trên">{g.stopOnly ? 'chỉ tạm dừng' : g.round ? 'tổng một ván' : 'một lượt'}{g.note ? `, ${g.note}` : ''}</td>
              </tr>
            );
          })}
        </tbody>
      </table> : <EmptyState title="Không có game khớp bộ lọc" />}
      <p className="note">Game hỏng vẫn có trong bảng để đặt sẵn hạn mức; ẩn hay hiện game ở trang Ẩn hiện game.</p>
      {draft.size ? (
        <div className="actionbar" role="region" aria-label="Thay đổi chưa lưu">
          <span><span className="num">{draft.size}</span> game đã đổi, chưa lưu</span>
          <Button kind="ghost" size="md" onClick={discard}>Bỏ thay đổi</Button>
          <Button kind="primary" size="md" disabled={invalid.length > 0 || stale} onClick={() => setSaving(true)}>Lưu</Button>
        </div>
      ) : null}
      <Modal open={saving} modalHeading={`Lưu hạn mức ${draft.size} game`} primaryButtonText="Gửi sang hệ A" secondaryButtonText="Hủy"
        primaryButtonDisabled={m.isPending} onRequestClose={() => setSaving(false)} onRequestSubmit={() => m.mutate()}>
        <div className="stack">
          <table className="plain">
            <thead><tr><th>Game</th><th>Trước</th><th>Sau</th></tr></thead>
            <tbody>
              {[...draft].map(([id, d]) => (
                <tr key={id}><td className="is-title">{byId.get(id)?.name ?? id}</td><td data-label="Trước">{describe(toDraft(byId.get(id)?.limit ?? null))}</td><td data-label="Sau">{describe(d)}</td></tr>
              ))}
            </tbody>
          </table>
          <InlineError error={stale ? null : m.error} />
        </div>
      </Modal>
    </div>
  );
}
